import React, { useState, useContext, useCallback, useEffect, useRef, useMemo } from 'react'
import styles from './MediaView.module.scss'
import { useQuery, useLazyQuery, useMutation } from '@apollo/client'
import {
  LIST_MEDIA_COLLECTIONS_QUERY,
  GET_MEDIA_COLLECTION_QUERY,
  CREATE_MEDIA_COLLECTION_MUTATION,
  CHECK_OUT_MEDIA_COLLECTION_MUTATION,
  CHECK_IN_MEDIA_COLLECTION_MUTATION,
  DELETE_MEDIA_COLLECTION_MUTATION,
  UPDATE_MEDIA_COLLECTION_DATE_MUTATION,
  BROWSE_MEDIA_FOLDER_QUERY,
  GET_PROJECTS_QUERY
} from '../query/gqlQueries'
import UserContext from '../context/UserContext'

import CloseIcon from '@mui/icons-material/Close'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import AddIcon from '@mui/icons-material/Add'
import RefreshIcon from '@mui/icons-material/Refresh'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import LockIcon from '@mui/icons-material/Lock'
import FolderIcon from '@mui/icons-material/Folder'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary'
import PlayCircleIcon from '@mui/icons-material/PlayCircle'

import MediaLibrary from './MediaLibrary'

// Normalize a filename for duplicate detection: drop the " (n)" collision
// suffix the image-server adds and lowercase, so "DSC09482.ARW" and
// "DSC09482 (1).ARW" collapse to the same key.
function dupeKey(filename) {
  const name = String(filename || '')
  const dot = name.lastIndexOf('.')
  const ext = dot > 0 ? name.slice(dot).toLowerCase() : ''
  const stem = (dot > 0 ? name.slice(0, dot) : name).replace(/\s*\(\d+\)$/, '')
  return stem.trim().toLowerCase() + ext
}

// File classification for the browse grid.
const VIDEO_EXTS = new Set(['.mp4', '.mov', '.m4v', '.avi', '.mkv'])
const SIDECAR_EXTS = new Set(['.xml', '.thm'])  // camera clip metadata, stored beside the video
const extOf = (name) => { const d = String(name || '').lastIndexOf('.'); return d > 0 ? name.slice(d).toLowerCase() : '' }
const stemOf = (name) => { const d = String(name || '').lastIndexOf('.'); return d > 0 ? name.slice(0, d) : String(name || '') }
const isVideoName = (name) => VIDEO_EXTS.has(extOf(name))
const isSidecarName = (name) => SIDECAR_EXTS.has(extOf(name))

// Human-friendly byte formatter: 1234567890 → "1.15 GB"
function formatBytes(bytes) {
  if (!bytes || bytes < 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let i = 0
  let n = bytes
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++ }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 2)} ${units[i]}`
}

// Mirror the backend slugify so the folder preview matches what'll be created.
function slugifyPreview(name) {
  return String(name || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '')
    .slice(0, 80)
}

// Group a flat list of shoots by their parent project title, preserving the
// server's ordering (project ASC, createdAt DESC). Shoots with no project
// (legacy data) fall under "Ungrouped".
function groupShootsByProject(shoots) {
  const groups = []
  const index = {}
  for (const shoot of shoots) {
    const project = shoot.properties.project || 'Ungrouped'
    if (!(project in index)) {
      index[project] = groups.length
      groups.push({ project, shoots: [] })
    }
    groups[index[project]].shoots.push(shoot)
  }
  return groups
}

// The GraphQL server bakes an absolute thumbnail URL using ITS
// IMAGE_STORE_PUBLIC_URL (which may be a stale 'localhost:4001'). Rewrite the
// origin to the frontend's REACT_APP_IMAGE_STORE_URI when set, so each client
// can point at the reachable image-server (LAN IP, public tunnel, …) without
// the backend having to know which client is asking. Keeps the /media/…
// path intact. No-op when the env var is unset or the URL can't be parsed.
const IMAGE_STORE_URI = process.env.REACT_APP_IMAGE_STORE_URI
const STORE_TOKEN = process.env.REACT_APP_IMAGE_STORE_TOKEN || ''
function thumbSrc(url) {
  if (!url) return url
  if (!IMAGE_STORE_URI) return url
  try {
    const u = new URL(url)
    return IMAGE_STORE_URI.replace(/\/$/, '') + u.pathname + u.search
  } catch {
    return url
  }
}

// Today as YYYY-MM-DD for the date picker default.
function todayISO() {
  return new Date().toISOString().split('T')[0]
}

// A YYYY-MM-DD (or ISO/Neo4j) shoot date → "11 Mar 2026". Empty → null.
function formatShootDate(val) {
  if (!val) return null
  try {
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
      const [y, m, d] = val.split('-').map(Number)
      return new Date(y, m - 1, d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    }
    const d = new Date(val)
    return isNaN(d.getTime()) ? null : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  } catch { return null }
}

// "2h ago", "yesterday", "Mar 11", "2024-03-11"
function formatRelative(iso) {
  if (!iso) return null
  let d
  try { d = new Date(iso) } catch (e) { return null }
  if (isNaN(d.getTime())) return null
  const now = Date.now()
  const ms = now - d.getTime()
  const s = Math.floor(ms / 1000)
  if (s < 60) return 'just now'
  if (s < 3600) return `${Math.floor(s / 60)}m ago`
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`
  if (s < 86400 * 2) return 'yesterday'
  if (s < 86400 * 7) return `${Math.floor(s / 86400)}d ago`
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: d.getFullYear() === new Date().getFullYear() ? undefined : 'numeric' })
}

export default function MediaView({ setIsVisible }) {
  const { username } = useContext(UserContext)
  const [libraryOpen, setLibraryOpen] = useState(false)
  const [selectedId, setSelectedId] = useState(null)
  // Folder drill-down: null = project detail, "RAW"|"PSD"|… = browsing that folder.
  const [selectedFolder, setSelectedFolder] = useState(null)
  // Lightbox: index into the current folder's photos, or null when closed.
  const [previewIdx, setPreviewIdx] = useState(null)
  // Web upload of files into the current folder.
  const [uploading, setUploading] = useState(false)
  const [uploadMsg, setUploadMsg] = useState(null)
  const uploadInputRef = useRef(null)
  const [isCreating, setIsCreating] = useState(false)
  const [newName, setNewName] = useState('')        // shoot name, e.g. "Audi A3"
  const [newProject, setNewProject] = useState('')  // parent project, e.g. "CleverCar"
  const [newNotes, setNewNotes] = useState('')
  const [newShootDate, setNewShootDate] = useState(todayISO())
  const [createError, setCreateError] = useState(null)
  // Inline shoot-date editing in the detail view.
  const [editingDate, setEditingDate] = useState(false)
  const [localDate, setLocalDate] = useState('')

  // Existing task-management projects, used to populate the project picker
  // so media shoots attach to the same projects the rest of the app uses.
  const { data: projectsData } = useQuery(GET_PROJECTS_QUERY, {
    variables: { username },
    skip: !username
  })
  const existingProjects = projectsData?.getProjects || []

  const { data: listData, loading: listLoading, refetch: refetchList } = useQuery(
    LIST_MEDIA_COLLECTIONS_QUERY,
    { variables: { username: null }, skip: !username, fetchPolicy: 'cache-and-network' }
  )

  const [fetchDetail, { data: detailData, loading: detailLoading, refetch: refetchDetail }] =
    useLazyQuery(GET_MEDIA_COLLECTION_QUERY, { fetchPolicy: 'cache-and-network' })

  const [fetchBrowse, { data: browseData, loading: browseLoading, refetch: refetchBrowse }] =
    useLazyQuery(BROWSE_MEDIA_FOLDER_QUERY, { fetchPolicy: 'cache-and-network' })

  const [createProject, { loading: creating }] = useMutation(CREATE_MEDIA_COLLECTION_MUTATION)
  const [checkOutProject] = useMutation(CHECK_OUT_MEDIA_COLLECTION_MUTATION)
  const [checkInProject] = useMutation(CHECK_IN_MEDIA_COLLECTION_MUTATION)
  const [deleteProject] = useMutation(DELETE_MEDIA_COLLECTION_MUTATION)
  const [updateShootDate] = useMutation(UPDATE_MEDIA_COLLECTION_DATE_MUTATION)

  const projects = listData?.listMediaCollections || []
  // Contents of the currently-open folder path — subfolders to drill into and
  // image files to show. Hoisted so the lightbox keyboard handler reaches them.
  const browse = browseData?.browseMediaFolder
  const photos = browse?.photos || []
  const browseFolders = browse?.folders || []

  // Fold sidecar files (Sony ".XML"/".THM" clip metadata) into their parent
  // video/image so each clip is one card. A sidecar attaches to the media file
  // whose stem it matches: Sony names them "C0843M01.XML" for "C0843.MP4", so we
  // strip a trailing "Mnn" and match, falling back to the longest stem that is a
  // prefix of the sidecar's name. Unmatched files stay as their own cards.
  const browseItems = useMemo(() => {
    const media = []
    const sidecars = []
    for (const p of photos) (isSidecarName(p.filename) ? sidecars : media).push(p)
    const items = media.map((m) => ({ ...m, sidecars: [] }))
    const stems = items.map((it) => ({ it, stem: stemOf(it.filename).toLowerCase() }))
    const leftover = []
    for (const s of sidecars) {
      const sStem = stemOf(s.filename).toLowerCase()
      const sBase = sStem.replace(/m\d+$/, '')
      let best = stems.find((x) => x.stem === sBase || x.stem === sStem)
      if (!best) {
        let bestLen = -1
        for (const x of stems) {
          if (x.stem && sStem.startsWith(x.stem) && x.stem.length > bestLen) { best = x; bestLen = x.stem.length }
        }
      }
      if (best) best.it.sidecars.push(s)
      else leftover.push({ ...s, sidecars: [] })
    }
    return [...items, ...leftover].sort((a, b) => a.filename.localeCompare(b.filename))
  }, [photos])

  // Suspected duplicates by filename (primary items only): the image-server
  // suffixes collisions as "X (1).ext", so items sharing a normalized key were
  // uploaded under the same base name more than once.
  const dupeCounts = useMemo(() => {
    const m = new Map()
    for (const p of browseItems) { const k = dupeKey(p.filename); m.set(k, (m.get(k) || 0) + 1) }
    return m
  }, [browseItems])
  const dupeTotal = useMemo(
    () => browseItems.reduce((n, p) => n + (dupeCounts.get(dupeKey(p.filename)) > 1 ? 1 : 0), 0),
    [browseItems, dupeCounts]
  )

  // Direct URL to an original file on the image-server (video inline playback,
  // or ?download=1 to save a sidecar). Mirrors how thumbnailUrl is composed —
  // identity is the folder-relative path.
  const mediaFileUrl = useCallback((photo, download) => {
    const cp = detailData?.getMediaCollection?.project?.properties
    if (!cp || !IMAGE_STORE_URI || !photo) return null
    const rel = String(photo.identity).split('/').map(encodeURIComponent).join('/')
    return `${IMAGE_STORE_URI.replace(/\/$/, '')}/media/file/${cp.projectSlug}/${cp.slug}/${rel}${download ? '?download=1' : ''}`
  }, [detailData])

  // Lightbox keyboard nav: ← → to move, Esc to close.
  useEffect(() => {
    if (previewIdx === null) return
    const onKey = (e) => {
      if (e.key === 'Escape') setPreviewIdx(null)
      else if (e.key === 'ArrowRight') setPreviewIdx((i) => (i === null ? i : Math.min(i + 1, browseItems.length - 1)))
      else if (e.key === 'ArrowLeft') setPreviewIdx((i) => (i === null ? i : Math.max(i - 1, 0)))
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [previewIdx, browseItems.length])

  const handleSelect = useCallback((id) => {
    setSelectedId(id)
    fetchDetail({ variables: { identity: id } })
  }, [fetchDetail])

  const handleBack = () => {
    setSelectedId(null)
    setSelectedFolder(null)
    setIsCreating(false)
    setCreateError(null)
    setNewName('')
    setNewProject('')
    setNewNotes('')
  }

  // Drill into a folder of a project — fetches its photos and triggers
  // server-side thumbnail caching as the grid renders.
  const handleOpenFolder = useCallback((folderPath) => {
    setSelectedFolder(folderPath)
    setPreviewIdx(null)
    fetchBrowse({ variables: { projectIdentity: selectedId, path: folderPath } })
  }, [selectedId, fetchBrowse])

  // Upload files straight into the current folder on the mini's drive. The
  // target path (project/collection/folder) is passed to the image-server,
  // which streams each file to disk keeping its original name.
  const handleUpload = useCallback(async (fileList) => {
    const files = Array.from(fileList || [])
    if (files.length === 0) return
    const collProps = detailData?.getMediaCollection?.project?.properties
    if (!collProps) return
    if (!IMAGE_STORE_URI) {
      window.alert('REACT_APP_IMAGE_STORE_URI is not set — cannot reach the media server.')
      return
    }

    // Warn before uploading names that duplicate files already in this folder
    // (or each other). The server won't overwrite — it suffixes " (n)" — but a
    // matching base name is usually an accidental re-upload, so let the user bail.
    const existingKeys = new Set(photos.map((p) => dupeKey(p.filename)))
    const seen = new Set()
    const dupes = []
    for (const f of files) {
      const k = dupeKey(f.name)
      if (existingKeys.has(k) || seen.has(k)) dupes.push(f.name)
      seen.add(k)
    }
    if (dupes.length > 0) {
      const proceed = window.confirm(
        `${dupes.length} of these look like duplicates of files already here:\n\n` +
        dupes.slice(0, 15).join('\n') + (dupes.length > 15 ? `\n…and ${dupes.length - 15} more` : '') +
        `\n\nUpload anyway? They'll be saved with a " (n)" suffix, not overwritten.`
      )
      if (!proceed) {
        if (uploadInputRef.current) uploadInputRef.current.value = ''
        return
      }
    }

    const rel = `${collProps.projectSlug}/${collProps.slug}${selectedFolder ? '/' + selectedFolder : ''}`
    const base = IMAGE_STORE_URI.replace(/\/$/, '')
    // One request per file (sizes never sum), uploaded consecutively. Through
    // the Cloudflare tunnel (https) each request body is capped at ~100 MB; on
    // the LAN IP (http) there's no such cap. Pre-flight remote uploads so a big
    // RAW fails with a clear message instead of a reset connection.
    const remote = /^https:/i.test(base)
    const REMOTE_CAP = 100 * 1024 * 1024

    setUploading(true)
    let ok = 0
    const failed = []
    for (let i = 0; i < files.length; i++) {
      const f = files[i]
      setUploadMsg(`${i + 1}/${files.length} — ${f.name}`)
      if (remote && f.size > REMOTE_CAP) {
        failed.push(`${f.name} — ${Math.round(f.size / 1048576)} MB is over the 100 MB remote limit; upload on the LAN or via SMB`)
        continue
      }
      const form = new FormData()
      form.append('files', f)
      try {
        const res = await fetch(`${base}/media/upload?path=${encodeURIComponent(rel)}`, {
          method: 'POST',
          headers: STORE_TOKEN ? { Authorization: `Bearer ${STORE_TOKEN}` } : {},
          body: form,
        })
        if (!res.ok) {
          const body = await res.json().catch(() => ({}))
          throw new Error(body.error || `HTTP ${res.status}`)
        }
        ok++
      } catch (e) {
        const m = String(e.message)
        failed.push(
          `${f.name} — ` +
          (m.includes('Load failed') || m.includes('Failed to fetch')
            ? 'connection lost (file too big for this route, or server unreachable)'
            : m)
        )
      }
    }

    setUploading(false)
    setUploadMsg(null)
    if (uploadInputRef.current) uploadInputRef.current.value = ''
    await fetchBrowse({ variables: { projectIdentity: selectedId, path: selectedFolder } })
    if (failed.length > 0) {
      window.alert(`${ok} uploaded, ${failed.length} failed:\n\n${failed.join('\n')}`)
    }
  }, [detailData, selectedFolder, selectedId, fetchBrowse, photos])

  // Back from the photo grid → project detail. Different from handleBack
  // (which exits all the way to the project list).
  const handleBackToProject = () => {
    setSelectedFolder(null)
    setPreviewIdx(null)
  }

  const handleCreate = useCallback(async (e) => {
    e?.preventDefault?.()
    if (!newName.trim() || !newProject.trim() || !username) return
    setCreateError(null)
    try {
      const { data } = await createProject({
        variables: {
          input: {
            name: newName.trim(),
            project: newProject.trim(),
            username,
            notes: newNotes.trim() || null,
            shootDate: newShootDate || null
          }
        }
      })
      const created = data?.createMediaCollection
      setIsCreating(false)
      setNewName('')
      setNewProject('')
      setNewNotes('')
      setNewShootDate(todayISO())
      await refetchList()
      if (created?.identity) handleSelect(created.identity)
    } catch (err) {
      setCreateError(err.message || 'Could not create shoot.')
    }
  }, [newName, newProject, newNotes, newShootDate, username, createProject, refetchList, handleSelect])

  const handleSaveDate = useCallback(async (id) => {
    if (!localDate) { setEditingDate(false); return }
    try {
      await updateShootDate({ variables: { identity: id, shootDate: localDate } })
      setEditingDate(false)
      await refetchDetail({ identity: id })
      await refetchList()
    } catch (err) {
      console.warn('updateShootDate failed', err)
    }
  }, [localDate, updateShootDate, refetchDetail, refetchList])

  const handleCheckOut = useCallback(async (id) => {
    if (!username) return
    try {
      await checkOutProject({ variables: { identity: id, username } })
      await refetchDetail({ identity: id })
      await refetchList()
    } catch (err) {
      console.warn('checkOut failed', err)
    }
  }, [username, checkOutProject, refetchDetail, refetchList])

  const handleCheckIn = useCallback(async (id) => {
    try {
      await checkInProject({ variables: { identity: id } })
      await refetchDetail({ identity: id })
      await refetchList()
    } catch (err) {
      console.warn('checkIn failed', err)
    }
  }, [checkInProject, refetchDetail, refetchList])

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm(
      'Delete this shoot from the registry?\n\n' +
      'Files on the external drive will NOT be touched — only the metadata + check-out state is removed. ' +
      'You can recreate the shoot with the same name to re-attach it.'
    )) return
    try {
      await deleteProject({ variables: { identity: id } })
      setSelectedId(null)
      await refetchList()
    } catch (err) {
      console.warn('delete failed', err)
    }
  }, [deleteProject, refetchList])

  if (!username) {
    return (
      <div className={styles.view}>
        <button className={styles.closeBtn} onClick={() => setIsVisible(false)}><CloseIcon /></button>
        <div className={styles.status}>Sign in to view projects.</div>
      </div>
    )
  }

  // ───────────────────── LIBRARY (originals, store-once) ─────────────────
  if (libraryOpen) {
    return (
      <MediaLibrary
        username={username}
        onBack={() => setLibraryOpen(false)}
        setIsVisible={setIsVisible}
      />
    )
  }

  // ───────────────────── PHOTO GRID (folder browse) ─────────────────────
  if (selectedId && selectedFolder && detailData?.getMediaCollection) {
    const projectName = detailData.getMediaCollection.project.properties.name

    return (
      <div className={styles.view}>
        <div className={styles.gridBg} />
        {previewIdx === null && (
          <button className={styles.closeBtn} onClick={() => setIsVisible(false)}><CloseIcon /></button>
        )}

        <div className={styles.container}>
          <header className={styles.header}>
            <div className={styles.breadcrumbs}>
              <button className={styles.crumb} onClick={handleBack}>Shoots</button>
              <span className={styles.crumbSep}>/</span>
              <button className={styles.crumb} onClick={handleBackToProject}>{projectName}</button>
              {selectedFolder.split('/').map((seg, i, arr) => {
                const cumulative = arr.slice(0, i + 1).join('/')
                const isLast = i === arr.length - 1
                return (
                  <React.Fragment key={cumulative}>
                    <span className={styles.crumbSep}>/</span>
                    {isLast
                      ? <span className={styles.crumbCurrent}>{seg}</span>
                      : <button className={styles.crumb} onClick={() => handleOpenFolder(cumulative)}>{seg}</button>}
                  </React.Fragment>
                )
              })}
            </div>
            <div className={styles.toolbar}>
              <input
                ref={uploadInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => handleUpload(e.target.files)}
              />
              <button
                className={`${styles.iconBtn} ${styles.primaryBtn}`}
                title={`Upload files into ${selectedFolder}`}
                disabled={uploading}
                onClick={() => uploadInputRef.current?.click()}>
                <FileUploadIcon fontSize="small" /> {uploading ? (uploadMsg ? `Uploading ${uploadMsg}` : 'Uploading…') : 'Upload'}
              </button>
              <button
                className={styles.iconBtn}
                title="Reload"
                onClick={() => fetchBrowse({ variables: { projectIdentity: selectedId, path: selectedFolder } })}>
                <RefreshIcon fontSize="small" />
              </button>
            </div>
          </header>

          {browseLoading && browseFolders.length === 0 && photos.length === 0 && (
            <div className={styles.status}>Scanning folder…</div>
          )}

          {browseFolders.length > 0 && (
            <div className={styles.folderGrid}>
              {browseFolders.map(f => (
                <div
                  key={f.path}
                  className={`${styles.folderCard} ${styles.folderCardBrowsable}`}
                  onClick={() => handleOpenFolder(f.path)}
                  title={`Open ${f.name}`}
                >
                  <div className={styles.folderHeader}>
                    <FolderIcon fontSize="small" style={{ color: '#94a3b8' }} />
                    <span className={styles.folderName}>{f.name}</span>
                  </div>
                  <div className={styles.folderStats}>
                    <div className={styles.statLine}>
                      <span className={styles.statNumber}>{f.fileCount.toLocaleString()}</span>
                      <span className={styles.statUnit}>files</span>
                    </div>
                    <div className={styles.statLine}>
                      <span className={styles.statNumber}>{formatBytes(f.totalBytes)}</span>
                    </div>
                    <div className={styles.statRecent}>
                      {f.lastModified ? `updated ${formatRelative(f.lastModified)}` : 'empty'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!browseLoading && browseFolders.length === 0 && photos.length === 0 && (
            <div className={styles.emptyState}>
              <p>Nothing in <strong>{selectedFolder}</strong> yet.</p>
              <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
                Add files into <code>{detailData.getMediaCollection.project.properties.basePath}/{selectedFolder}/</code> via Finder, then hit Reload.
              </p>
            </div>
          )}

          {dupeTotal > 0 && (
            <div className={styles.dupeNotice}>
              <strong>{dupeTotal} possible duplicate{dupeTotal > 1 ? 's' : ''}</strong> in this folder — files sharing a base name (e.g. “X.ARW” and “X (1).ARW”). Flagged below.
            </div>
          )}

          <div className={styles.photoGrid}>
            {browseItems.map((photo, i) => {
              const isDupe = dupeCounts.get(dupeKey(photo.filename)) > 1
              const isVideo = isVideoName(photo.filename)
              return (
              <div key={photo.identity} className={`${styles.photoCard} ${isDupe ? styles.photoCardDupe : ''}`} onClick={() => setPreviewIdx(i)}>
                <div className={styles.photoThumb}>
                  {isDupe && (
                    <div className={styles.dupeBadge} title="Possible duplicate — shares a base filename with another file in this folder">
                      dup
                    </div>
                  )}
                  {photo.thumbnailUrl ? (
                    <img
                      src={thumbSrc(photo.thumbnailUrl)}
                      alt={photo.filename}
                      loading="lazy"
                      onError={(e) => { e.currentTarget.style.opacity = 0.2 }}
                    />
                  ) : (
                    <div className={styles.photoThumbPlaceholder}>
                      {isVideo ? <PlayCircleIcon fontSize="small" /> : <FolderIcon fontSize="small" />}
                      <span>{isVideo ? 'video' : 'no preview'}</span>
                    </div>
                  )}
                  {isVideo && photo.thumbnailUrl && (
                    <div className={styles.playOverlay}><PlayCircleIcon /></div>
                  )}
                </div>
                <div className={styles.photoMeta}>
                  <div className={styles.photoName} title={photo.filename}>{photo.filename}</div>
                  <div className={styles.photoStats}>
                    <span>{formatBytes(photo.size)}</span>
                    <span>{formatRelative(photo.mtime)}</span>
                  </div>
                  {photo.sidecars && photo.sidecars.length > 0 && (
                    <div className={styles.sidecarRow}>
                      {photo.sidecars.map((s) => (
                        <a
                          key={s.identity}
                          className={styles.sidecarChip}
                          href={mediaFileUrl(s, true)}
                          download={s.filename}
                          onClick={(e) => e.stopPropagation()}
                          title={`Download ${s.filename}`}
                        >
                          {extOf(s.filename).replace('.', '').toUpperCase() || 'FILE'}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              )
            })}
          </div>

          {previewIdx !== null && browseItems[previewIdx] && (
            <div className={styles.lightbox} onClick={() => setPreviewIdx(null)}>
              <button className={styles.lightboxClose} onClick={() => setPreviewIdx(null)} title="Close (Esc)">
                <CloseIcon />
              </button>
              {previewIdx > 0 && (
                <button
                  className={`${styles.lightboxNav} ${styles.lightboxPrev}`}
                  onClick={(e) => { e.stopPropagation(); setPreviewIdx(previewIdx - 1) }}
                  title="Previous (←)">
                  <NavigateBeforeIcon />
                </button>
              )}
              <div className={styles.lightboxInner} onClick={(e) => e.stopPropagation()}>
                {isVideoName(browseItems[previewIdx].filename) ? (
                  <video
                    key={browseItems[previewIdx].identity}
                    className={styles.lightboxVideo}
                    src={mediaFileUrl(browseItems[previewIdx])}
                    poster={browseItems[previewIdx].thumbnailUrl ? thumbSrc(browseItems[previewIdx].thumbnailUrl) : undefined}
                    controls
                    autoPlay
                    preload="metadata"
                  />
                ) : browseItems[previewIdx].thumbnailUrl ? (
                  <img src={thumbSrc(browseItems[previewIdx].thumbnailUrl)} alt={browseItems[previewIdx].filename} />
                ) : (
                  <div className={styles.lightboxNoPreview}>No preview for this file type</div>
                )}
                <div className={styles.lightboxCaption}>
                  {browseItems[previewIdx].filename} · {formatBytes(browseItems[previewIdx].size)} · {previewIdx + 1} / {browseItems.length}
                  {browseItems[previewIdx].sidecars && browseItems[previewIdx].sidecars.length > 0 && (
                    <span className={styles.lightboxSidecars}>
                      {browseItems[previewIdx].sidecars.map((s) => (
                        <a
                          key={s.identity}
                          className={styles.sidecarChip}
                          href={mediaFileUrl(s, true)}
                          download={s.filename}
                          onClick={(e) => e.stopPropagation()}
                          title={`Download ${s.filename}`}
                        >
                          ⬇ {s.filename}
                        </a>
                      ))}
                    </span>
                  )}
                </div>
              </div>
              {previewIdx < browseItems.length - 1 && (
                <button
                  className={`${styles.lightboxNav} ${styles.lightboxNext}`}
                  onClick={(e) => { e.stopPropagation(); setPreviewIdx(previewIdx + 1) }}
                  title="Next (→)">
                  <NavigateNextIcon />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    )
  }

  // ───────────────────── DETAIL VIEW ─────────────────────
  if (selectedId && detailData?.getMediaCollection) {
    const { project, folders } = detailData.getMediaCollection
    const p = project.properties
    const isCheckedOut = !!p.checkedOutBy
    const isCheckedOutByMe = p.checkedOutBy === username
    const isCheckedOutByOther = isCheckedOut && !isCheckedOutByMe
    const totalBytes = folders.reduce((sum, f) => sum + (f.totalBytes || 0), 0)
    const totalFiles = folders.reduce((sum, f) => sum + (f.fileCount || 0), 0)

    return (
      <div className={styles.view}>
        <div className={styles.gridBg} />
        <button className={styles.closeBtn} onClick={() => setIsVisible(false)}><CloseIcon /></button>

        <div className={styles.container}>
          <header className={styles.header}>
            <button className={styles.backBtn} onClick={handleBack}>
              <ArrowBackIcon fontSize="small" /> Shoots
            </button>
            <div className={styles.titleGroup}>
              <div className={styles.label}>{p.project || 'Media'}</div>
              <h1 className={styles.title}>{p.name}</h1>
              {p.notes && <p className={styles.notes}>{p.notes}</p>}
            </div>
            <div className={styles.toolbar}>
              <button className={styles.iconBtn} title="Reload" onClick={() => refetchDetail({ identity: selectedId })}>
                <RefreshIcon fontSize="small" />
              </button>
              <button className={`${styles.iconBtn} ${styles.dangerBtn}`} title="Delete shoot" onClick={() => handleDelete(selectedId)}>
                <DeleteOutlineIcon fontSize="small" />
              </button>
            </div>
          </header>

          <div className={styles.detailMeta}>
            <div className={styles.metaRow}>
              <span className={styles.metaLabel}>Folder</span>
              <span className={styles.metaValue} title={p.basePath}>{p.basePath}</span>
            </div>
            <div className={styles.metaRow}>
              <span className={styles.metaLabel}>Totals</span>
              <span className={styles.metaValue}>{totalFiles.toLocaleString()} files · {formatBytes(totalBytes)}</span>
            </div>
            <div className={styles.metaRow}>
              <span className={styles.metaLabel}>Shoot date</span>
              {editingDate ? (
                <span className={styles.metaValue} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input
                    type="date"
                    className={styles.input}
                    style={{ padding: '0.2rem 0.4rem', width: 'auto' }}
                    value={localDate}
                    onChange={e => setLocalDate(e.target.value)}
                    autoFocus
                  />
                  <button className={styles.iconBtn} onClick={() => handleSaveDate(selectedId)} title="Save">✓</button>
                  <button className={styles.iconBtn} onClick={() => setEditingDate(false)} title="Cancel">✕</button>
                </span>
              ) : (
                <span
                  className={styles.metaValue}
                  style={{ cursor: 'pointer', textDecoration: 'underline dotted', textUnderlineOffset: '3px' }}
                  onClick={() => { setLocalDate(p.shootDate || todayISO()); setEditingDate(true) }}
                  title="Click to set the shoot date"
                >
                  {formatShootDate(p.shootDate) || 'Set date'}
                </span>
              )}
            </div>
            <div className={styles.metaRow}>
              <span className={styles.metaLabel}>Created by</span>
              <span className={styles.metaValue}>{p.username} · {formatRelative(p.createdAt)}</span>
            </div>
          </div>

          <div className={styles.checkoutPanel}>
            {!isCheckedOut && (
              <>
                <div className={styles.checkoutStatus}>
                  <LockOpenIcon fontSize="small" style={{ color: '#6ee7b7' }} />
                  <span>Available — no one is editing</span>
                </div>
                <button className={`${styles.iconBtn} ${styles.primaryBtn}`} onClick={() => handleCheckOut(selectedId)}>
                  <LockIcon fontSize="small" /> Check out
                </button>
              </>
            )}
            {isCheckedOutByMe && (
              <>
                <div className={styles.checkoutStatus}>
                  <LockIcon fontSize="small" style={{ color: '#fbbf24' }} />
                  <span>You have this checked out — {formatRelative(p.checkedOutAt)}</span>
                </div>
                <button className={`${styles.iconBtn} ${styles.primaryBtn}`} onClick={() => handleCheckIn(selectedId)}>
                  <LockOpenIcon fontSize="small" /> Check in
                </button>
              </>
            )}
            {isCheckedOutByOther && (
              <>
                <div className={styles.checkoutStatus}>
                  <LockIcon fontSize="small" style={{ color: '#f87171' }} />
                  <span><strong>{p.checkedOutBy}</strong> has this checked out — {formatRelative(p.checkedOutAt)}</span>
                </div>
                <button
                  className={`${styles.iconBtn} ${styles.warningBtn}`}
                  onClick={() => {
                    if (window.confirm(`${p.checkedOutBy} currently has this shoot checked out. Take it over?`)) {
                      handleCheckOut(selectedId)
                    }
                  }}>
                  <LockIcon fontSize="small" /> Take over
                </button>
                <button className={styles.iconBtn} onClick={() => handleCheckIn(selectedId)}>
                  Force check in
                </button>
              </>
            )}
          </div>

          <div className={styles.folderGrid}>
            {folders.map(f => {
              return (
                <div
                  key={f.name}
                  className={`${styles.folderCard} ${styles.folderCardBrowsable}`}
                  onClick={() => handleOpenFolder(f.path || f.name)}
                  title={`Open ${f.name}`}
                >
                  <div className={styles.folderHeader}>
                    <FolderIcon fontSize="small" style={{ color: '#94a3b8' }} />
                    <span className={styles.folderName}>{f.name}</span>
                  </div>
                  <div className={styles.folderStats}>
                    <div className={styles.statLine}>
                      <span className={styles.statNumber}>{f.fileCount.toLocaleString()}</span>
                      <span className={styles.statUnit}>files</span>
                    </div>
                    <div className={styles.statLine}>
                      <span className={styles.statNumber}>{formatBytes(f.totalBytes)}</span>
                    </div>
                    {f.lastModified && (
                      <div className={styles.statRecent}>
                        last touched {formatRelative(f.lastModified)}
                      </div>
                    )}
                    {!f.lastModified && (
                      <div className={styles.statRecent}>empty</div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          <div className={styles.workflowHint}>
            Click a folder to open it. Import folders nest by device (Sony · iPhone · GoPro). Add files via Finder/Lightroom at the path above.
          </div>
        </div>
      </div>
    )
  }

  // ───────────────────── LIST VIEW ─────────────────────
  return (
    <div className={styles.view}>
      <div className={styles.gridBg} />
      <button className={styles.closeBtn} onClick={() => setIsVisible(false)}><CloseIcon /></button>

      <div className={styles.container}>
        <header className={styles.header}>
          <div className={styles.titleGroup}>
            <div className={styles.label}>Media</div>
            <h1 className={styles.title}>Shoots</h1>
          </div>
          <div className={styles.toolbar}>
            <button className={styles.iconBtn} onClick={() => setLibraryOpen(true)} title="Browse originals library">
              <PhotoLibraryIcon fontSize="small" /> Library
            </button>
            <button className={`${styles.iconBtn} ${styles.primaryBtn}`} onClick={() => setIsCreating(true)}>
              <AddIcon fontSize="small" /> New Shoot
            </button>
            <button className={styles.iconBtn} onClick={() => refetchList()} title="Reload">
              <RefreshIcon fontSize="small" />
            </button>
          </div>
        </header>

        {isCreating && (
          <form className={styles.createForm} onSubmit={handleCreate}>
            <div className={styles.createField}>
              <label className={styles.fieldLabel}>Project</label>
              <input
                type="text"
                className={styles.input}
                value={newProject}
                onChange={e => setNewProject(e.target.value)}
                placeholder="e.g. CleverCar — pick existing or type a new one"
                list="media-projects"
                autoFocus
              />
              <datalist id="media-projects">
                {existingProjects.map(pr => (
                  <option key={pr.identity} value={pr.properties.title} />
                ))}
              </datalist>
            </div>
            <div className={styles.createField}>
              <label className={styles.fieldLabel}>Shoot name</label>
              <input
                type="text"
                className={styles.input}
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="e.g. Audi A3"
              />
              {(newProject.trim() || newName.trim()) && (
                <div className={styles.slugPreview}>
                  Folder: <code>{slugifyPreview(newProject) || '(project)'}/{slugifyPreview(newName) || '(shoot)'}</code>
                </div>
              )}
            </div>
            <div className={styles.createField}>
              <label className={styles.fieldLabel}>Shoot date</label>
              <input
                type="date"
                className={styles.input}
                value={newShootDate}
                onChange={e => setNewShootDate(e.target.value)}
              />
            </div>
            <div className={styles.createField}>
              <label className={styles.fieldLabel}>Notes (optional)</label>
              <textarea
                className={styles.input}
                value={newNotes}
                onChange={e => setNewNotes(e.target.value)}
                placeholder="Client, shoot date, car spec, anything worth remembering"
                rows={2}
              />
            </div>
            {createError && <div className={styles.errorMsg}>{createError}</div>}
            <div className={styles.createActions}>
              <button type="button" className={styles.iconBtn} onClick={() => { setIsCreating(false); setNewName(''); setNewProject(''); setNewNotes(''); setCreateError(null) }}>
                Cancel
              </button>
              <button type="submit" className={`${styles.iconBtn} ${styles.primaryBtn}`} disabled={creating || !newName.trim() || !newProject.trim()}>
                {creating ? 'Creating…' : 'Create + scaffold folders'}
              </button>
            </div>
          </form>
        )}

        {listLoading && projects.length === 0 && <div className={styles.status}>Loading…</div>}
        {!listLoading && projects.length === 0 && !isCreating && (
          <div className={styles.emptyState}>
            <p>No shoots yet.</p>
            <button className={`${styles.iconBtn} ${styles.primaryBtn}`} onClick={() => setIsCreating(true)}>
              <AddIcon fontSize="small" /> Create the first shoot
            </button>
          </div>
        )}

        {groupShootsByProject(projects).map(group => (
          <div key={group.project} className={styles.projectGroup}>
            <div className={styles.projectGroupHeader}>{group.project}</div>
            <div className={styles.projectGrid}>
              {group.shoots.map(proj => {
                const pp = proj.properties
                const checkedOut = !!pp.checkedOutBy
                const byMe = pp.checkedOutBy === username
                return (
                  <div key={proj.identity} className={styles.projectCard} onClick={() => handleSelect(proj.identity)}>
                    <div className={styles.projectCardHeader}>
                      <div className={styles.projectName}>{pp.name}</div>
                      {checkedOut && (
                        <span className={`${styles.checkoutBadge} ${byMe ? styles.checkoutByMe : styles.checkoutByOther}`}>
                          <LockIcon style={{ fontSize: 12 }} />
                          {byMe ? 'you' : pp.checkedOutBy}
                        </span>
                      )}
                    </div>
                    {pp.notes && <div className={styles.projectNotes}>{pp.notes}</div>}
                    <div className={styles.projectMeta}>
                      <span className={styles.slugTag}>{pp.slug}</span>
                      <span>{formatShootDate(pp.shootDate) || formatRelative(pp.createdAt)}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
